/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-02-14 18:52:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import moment from 'moment-timezone';
import { store } from 'src/stores/store';
import { APP_LANGUAGE_ENGLISH_VALUE } from 'src/constants/language-setting';
import { DATE_FORMAT } from 'src/constants/language-setting';

/**
 * Convert date object to string
 *
 * @param  {object} date date object
 *
 * @return {string}      date string
 *                       Ex: 2020-02-21T00:00:00.000Z
 */
export const convertDateToString = (date: Date) => {
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();

  return moment(Date.UTC(year, month, day, 0, 0, 0)).toISOString();
};

/**
 * Convert date string to object
 *
 * @param  {string} stringDate date string
 *                             Ex: 2020-02-21T00:00:00.000Z
 *
 * @return {object}            date object
 */
export const convertStringToDate = (stringDate: string) => {
  const date = new Date(stringDate);
  const day = date.getUTCDate();
  const month = date.getUTCMonth();
  const year = date.getUTCFullYear();

  return new Date(year, month, day);
};

/**
 * Get date with store timezone
 *
 *  * @return {Date} UTC Date
 */
export const getStoreDate = (date: Date | string | number) => {
  // Check type of date is string/object/number
  const dateValue =
    typeof date === 'string'
      ? new Date(date)
      : typeof date === 'number'
      ? new Date(date * 1000)
      : date;

  // Format date with language
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDateNumber = DATE_FORMAT[appLanguage].number;
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  if (timezone) {
    const storeDate = moment.tz(dateValue.toISOString(), timezone).format(formatDateNumber);
    return moment(storeDate, formatDateNumber).toDate();
  }
  return dateValue;
};

/**
 * Get time with store timezone
 *
 *  * @return {Date} UTC Date
 */
export const getStoreTime = (date: Date | string | number) => {
  // Check type of date is string/object/number
  const dateValue =
    typeof date === 'string'
      ? new Date(date)
      : typeof date === 'number'
      ? new Date(date * 1000)
      : date;

  // Format date with language
  const currentState = store.getState();

  let hour = dateValue.getHours();
  let minute = dateValue.getMinutes();

  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  if (timezone) {
    const storeDate = moment.tz(dateValue.toISOString(), timezone);
    hour = parseInt(storeDate.format('HH'));
    minute = parseInt(storeDate.format('mm'));
  }
  return { hour, minute };
};

/**
 * Get UTC date from date with store timezone
 *
 *  * @return {Date} UTC Date
 */
export const getUTCDate = (
  date: Date,
  hour: number = 0,
  minute: number = 0,
  second: number = 0
) => {
  // Create object date from input
  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();
  let dateValue = { year, month, day, hour, minute, second };

  const currentState = store.getState();
  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  if (timezone) {
    return moment.tz(dateValue, timezone);
  }
  return moment(dateValue);
};

/**
 * Get time with UTC time
 *
 *  * @return {Date} UTC Date
 */
export const getUTCTime = (time: number) => {
  const dateValue = new Date();
  let hour = Math.floor(time / 60);
  let minute = time % 60;
  // Format date with language
  const currentState = store.getState();

  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  if (timezone) {
    dateValue.setHours(hour);
    dateValue.setMinutes(minute);

    const storeDate = moment.tz(dateValue, timezone);
    hour = parseInt(storeDate.format('HH'));
    minute = parseInt(storeDate.format('mm'));
  }
  return { hour, minute };
};

/**
 * Show display date range
 *
 * @param  {Date} fromDate the start date
 * @param  {Date} toDate the end date
 *
 *  * @return {Object} save product
 */
export const displayDateRange = (fromDate: Object, toDate: Object) => {
  // Format date with language
  const currentState = store.getState();
  const appLanguage = _.get(
    currentState,
    'oauth.tenantApp.appLanguage',
    APP_LANGUAGE_ENGLISH_VALUE
  );
  const formatDate = DATE_FORMAT[appLanguage];

  const start = moment.isMoment(fromDate) ? fromDate : moment(fromDate);
  const end = moment.isMoment(toDate) ? toDate : moment(toDate);

  if (moment(end.format(formatDate.number)).isSame(start.format(formatDate.number))) {
    return moment(fromDate).format(formatDate.longDate);
  } else {
    if (start.year() === end.year()) {
      return `${start.format(formatDate.shortDate)} - ${end.format(formatDate.longDate)}`;
    } else {
      return `${start.format(formatDate.longDate)} - ${end.format(formatDate.longDate)}`;
    }
  }
};

export const isValidDate = date => {
  return date && Object.prototype.toString.call(date) === '[object Date]' && !isNaN(date);
};

/**
 * Display date with store timezone
 *
 *  * @return {Date} UTC Date
 */
export const displayDate = (date: Date | string | number, format: string) => {
  // Check type of date is string/object/number
  const dateValue =
    typeof date === 'string'
      ? new Date(date)
      : typeof date === 'number'
      ? new Date(date * 1000)
      : date;

  return moment(dateValue).format(format);
};

/**
 * Display date with store timezone
 *
 *  * @return {Date} UTC Date
 */
export const displayStoreDate = (date: Date | string | number, format: string) => {
  // Check type of date is string/object/number
  const dateValue =
    typeof date === 'string'
      ? new Date(date)
      : typeof date === 'number'
      ? new Date(date * 1000)
      : date;

  const currentState = store.getState();

  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  if (timezone) {
    const storeDate = moment.tz(dateValue?.toISOString(), timezone).format(format);
    return storeDate;
  }
  return moment(dateValue).format(format);
};

export const displayStoreDateUTC = (date: Date | string | number, format: string) => {
  // Check type of date is string/object/number
  const dateValue =
    typeof date === 'string'
      ? new Date(date)
      : typeof date === 'number'
      ? new Date(date * 1000)
      : date;

  const currentState = store.getState();

  const timezone = _.get(currentState, `oauth.tenantApp.externalInfo.iana_timezone`, '');
  if (timezone) {
    const storeDate = moment.utc(dateValue, timezone).format(format);
    return storeDate;
  }
  return moment(dateValue).format(format);
};
